import React, { useCallback } from 'react';

import { link } from '@aline/routes';
import { Box, Typography } from '@mui/material';
import clsx from 'clsx';
import { Sparkles } from 'lucide-react';
import type { PlateElementProps } from 'platejs/react';
import { PlateElement, useReadOnly, useSelected } from 'platejs/react';
import { openLink } from 'src/helpers/url';
import { makeStyles } from 'tss-react/mui';

import { SKILL_ICON_COLOR } from './skill-appearance';
import { getIsSkillElement } from './utils';
import {
  getIsPlainLeftClick,
  getLinkChipWrapperAttributes,
  LINK_CHIP_ANCHOR_PROPS,
} from '../../AlineMarkdownEditor/utils/link-chip';
import { ChipRemoveIcon, getChipRemoveButtonStyles, getEditorChipStyles } from '../utils';

const SKILL_CHIP_DATA_ATTRS = {
  skillId: 'data-skill-id',
  skillVersionId: 'data-skill-version-id',
} as const;

const iconContainerStyle = {
  alignItems: 'center',
  // The colour a skill carries everywhere else it is listed, rather than the chip's own.
  color: SKILL_ICON_COLOR,
  display: 'inline-flex',
  flexShrink: 0,
  height: 16,
  justifyContent: 'center',
  width: 16,
};

const useStyles = makeStyles()(theme => ({
  ...getChipRemoveButtonStyles(),
  skillElement: {
    ...getEditorChipStyles(theme),
    '&, &:hover, &:focus': {
      textDecoration: 'none',
    },
  },
  skillElementSelected: {
    borderColor: theme.palette.primary.main,
  },
  testVersionTag: {
    backgroundColor: theme.palette.action.selected,
    borderRadius: 4,
    color: theme.palette.text.secondary,
    flexShrink: 0,
    fontSize: 10,
    fontWeight: 600,
    lineHeight: '16px',
    padding: '0 4px',
  },
}));

/**
 * Chip standing in for a pinned skill.
 *
 * Renders like an @-mention chip, with an icon, a label and a remove button, because to the
 * reader it is the same kind of thing: a reference the message carries but does not spell
 * out. The server resolves the instructions from the chip's ids.
 */
export const SkillElement = React.memo(function SkillElement(
  props: PlateElementProps
): React.JSX.Element {
  const { classes } = useStyles();
  const readOnly = useReadOnly();
  const selected = useSelected();
  const { editor, element } = props;
  const isSkill = getIsSkillElement(element);
  const label = isSkill ? element.value : 'Skill';
  const accessibleLabel =
    isSkill && element.skillDescription ? `${label}: ${element.skillDescription}` : label;
  const skillId = isSkill ? element.skillId : null;

  const handleRemove = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      /**
       * Guarded because `at: undefined` is not a no-op: Slate falls back to the current
       * selection, so a chip whose path no longer resolves would delete whatever the user
       * had selected instead of itself.
       */
      const path = editor.api.findPath(element);
      if (!path) return;
      editor.tf.removeNodes({ at: path });
    },
    [editor, element]
  );

  const handleOpen = useCallback(
    (event: React.MouseEvent) => {
      if (!getIsPlainLeftClick(event)) return;
      event.preventDefault();
      event.stopPropagation();
      if (skillId) openLink(link.AdminAgentSkillDetail({ skillId }), { title: label });
    },
    [label, skillId]
  );

  return (
    <PlateElement
      {...props}
      as="span"
      attributes={{
        ...props.attributes,
        contentEditable: false,
        ...getLinkChipWrapperAttributes(accessibleLabel),
        ...(isSkill && {
          [SKILL_CHIP_DATA_ATTRS.skillId]: element.skillId,
          [SKILL_CHIP_DATA_ATTRS.skillVersionId]: element.skillVersionId,
        }),
      }}
    >
      <Box
        {...LINK_CHIP_ANCHOR_PROPS}
        className={clsx(classes.skillElement, selected && classes.skillElementSelected)}
        component="a"
        href={skillId ? link.AdminAgentSkillDetail({ skillId }) : undefined}
        onClick={handleOpen}
        onMouseDown={stopMouseDown}
      >
        {props.children}
        <Box component="span" sx={iconContainerStyle}>
          <Sparkles size={14} strokeWidth={2} />
        </Box>
        <Typography noWrap component="span">
          {label}
        </Typography>
        {isSkill && element.skillIsTestVersion && (
          <span className={classes.testVersionTag}>Draft</span>
        )}
        {!readOnly && (
          <span className={classes.chipRemoveButton} onClick={handleRemove}>
            <ChipRemoveIcon />
          </span>
        )}
      </Box>
    </PlateElement>
  );
});

function stopMouseDown(event: React.MouseEvent) {
  if (getIsPlainLeftClick(event)) event.preventDefault();
  event.stopPropagation();
}
